import React from 'react'
import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useTranslation } from 'react-i18next'
import { useZtSubsetDataContext } from '../context'
import DataSkeleton from '../dataSkeleton'
import { formatEmpty } from '../utils'

echarts.registerTheme('theme_jekka', {
	backgroundColor: '#fff'
})

const ChartSeven: React.FC = () => {
	const { t } = useTranslation()
	const { dataList, infoData, chartLoading, filters } = useZtSubsetDataContext()
	const { outbound_scene } = filters

	/** 是否异常件外呼 */
	const isExceptionScene = outbound_scene == 'delivery_fail'

	/** 图表配置 */
	const chartConfig = {
		/** 标题 */
		title: isExceptionScene
			? t('c_a_outbound_call_effective_quantity_tips')
			: t('c_a_outbound_call_feedback'),

		/** 第一组series名称 */
		firstSeriesName: isExceptionScene
			? t('c_a_outbound_call_number')
			: t('c_a_outbound_call_received'),

		/** 第二组series名称 */
		secondSeriesName: isExceptionScene
			? t('c_a_outbound_call_returned')
			: t('c_a_outbound_call_not_received'),

		/** 第一组数据 */
		firstSeriesData: isExceptionScene
			? dataList?.connection_redispatch_count
			: dataList?.connection_return_count,

		/** 第二组数据 */
		secondSeriesData: isExceptionScene
			? dataList?.connection_return_count
			: dataList?.connection_redispatch_count,

		/** 第一组颜色 */
		firstColor: isExceptionScene ? '#ffb72b' : '#6CE565',

		/** 第二组颜色 */
		secondColor: isExceptionScene ? '#6CE565' : '#ffb72b'
	}

	const optionList = {
		tooltip: {
			trigger: 'axis',
			confine: true,
			formatter: (params: any) => {
				if (!params || params.length === 0) return ''

				const lines: string[] = []

				/** 已收到/未收到场景 */
				if (!isExceptionScene) {
					const idx = params[0]?.dataIndex ?? 0
					const connectionCount = dataList?.connection_count?.[idx] ?? 0

					lines.push(`
                        <div style="margin-bottom:6px; display:flex; align-items:center;">
                            <span>${t('c_a_outbound_call_effective_quantity')}：</span>
                            <span style="font-weight:bold;">${connectionCount}</span>
                        </div>
                    `)
				} else {
					/** 异常件外呼场景 */
					lines.push(`
                        <div style="margin-bottom:8px;font-weight:bold;">
                            ${t('c_a_outbound_call_effective_quantity')}
                        </div>
                    `)
				}

				params.forEach((item: any) => {
					const value = item.value ?? 0

					lines.push(`
                        <div style="margin-bottom:6px; display:flex; align-items:center;">
                            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${item.color};margin-right:6px;"></span>
                            <span>${item.seriesName}：</span>
                            <span style="font-weight:bold;">${value}</span>
                        </div>
                    `)
				})

				return lines.join('')
			}
		},

		legend: null,

		grid: {
			left: 10,
			right: 10,
			bottom: 12,
			containLabel: true
		},

		xAxis: {
			type: 'category',
			data: dataList?.date // MM.DD
		},

		yAxis: {
			type: 'value'
		},

		series: [
			/** 效接通量/重派数量 */
			{
				type: 'bar',

				name: chartConfig.firstSeriesName,

				data: (chartConfig.firstSeriesData ?? []).map((item: any, idx: number) => ({
					value: item,
					itemStyle: {
						color: chartConfig.firstColor,
						borderRadius: (chartConfig.secondSeriesData?.[idx] ?? 0) > 0 ? 0 : [6, 6, 0, 0]
					}
				})),

				itemStyle: {
					color: chartConfig.firstColor
				},

				label: {
					show: chartConfig.firstSeriesData?.length < 18,
					color: '#fff',
					formatter: (params: any) => formatEmpty(params.value)
				},

				stack: 'resolve',

				barWidth: chartConfig.firstSeriesData?.length < 10 ? 36 : 'auto'
			},

			/** 效接通量/退回数量 */
			{
				type: 'bar',

				name: chartConfig.secondSeriesName,

				data: (chartConfig.secondSeriesData ?? []).map((item: any) => item),

				itemStyle: {
					color: chartConfig.secondColor,
					borderRadius: [6, 6, 0, 0]
				},

				label: {
					show: chartConfig.secondSeriesData?.length < 18,
					formatter: (params: any) => formatEmpty(params.value)
				},

				stack: 'resolve',

				barWidth: chartConfig.secondSeriesData?.length < 10 ? 36 : 'auto'
			}
		]
	}

	return (
		<div className="bg-white rounded-lg overflow-hidden px-4 w-[49.5%]">
			<div className="mt-[18px]">
				<div className="text-[14px] font-medium mb-[12px]">
					{t('c_a_outbound_call_effective_quantity')}
				</div>

				<div className="text-[14px] mb-[8px]">{chartConfig.title}</div>

				<div className="text-[18px] font-medium flex items-center">
					<DataSkeleton value={infoData?.total?.connection_count} />
				</div>
			</div>

			<ReactECharts theme="theme_jekka" option={optionList} showLoading={chartLoading} />
		</div>
	)
}

export default ChartSeven
